/* eslint-disable @typescript-eslint/no-use-before-define */

import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import useCompanyContext from 'common/hooks/useCompanyContext';
import SuspenseQuery from 'components/SuspenseQuery';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import companyProfileStorage from './companyProfileStorage';
import { BusinessInfoModuleDataDocument } from './graphql/queries/businessInfoModuleData.gql';
import machineConfig, { model, State } from './machine';
import { Context } from './machine/model';
import { StringifiedState } from './machine/state';
import ApeDetails from './pages/ApeDetails';
import CompanyIneligible from './pages/CompanyIneligible';
import ContactSupport from './pages/ContactSupport';
import IdentityInfoEdit from './pages/IdentityInfoEdit';
import InfoVerification from './pages/InfoVerification';
import IsMicro from './pages/IsMicro';
import LegalFormQuestion from './pages/LegalFormQuestion';
import LegalInfoEdit from './pages/LegalInfoEdit';
import ResumeOnboarding from './pages/ResumeOnboarding';
import Search from './pages/Search';

type BusinessInfoProps = {
  companyProfileId?: string;
};

const BusinessInfo = asBentoModule<BusinessInfoProps>(
  ({ companyProfileId, onDone }) => {
    const businessInfoQuery = useQuery(BusinessInfoModuleDataDocument);
    const companyContext = useCompanyContext();

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={businessInfoQuery}
      >
        {(businessInfoData) => (
          <BusinessInfoComponent
            moduleData={{
              businessInfoData,
              companyContext,
              existingCompanyProfileId: companyProfileId,
            }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

const BusinessInfoComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    // TODO(LED-2129): Fix Bento state machine context typing (currently any)
    const { companyProfile } = state.context as Context;
    const userProfile = moduleData.businessInfoData.viewer.profile;

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    const stringifiedState = stringifyState<StringifiedState>(state);

    // This will be true for the 3 edition states accessible from INFO_VERIFICATION state
    const isEditing = stringifiedState.includes('edit');

    switch (stringifiedState) {
      case State.RESUME_ONBOARDING: {
        const storedCompany = companyProfileStorage.get();

        if (storedCompany === null) {
          send(model.events.RESTART_ONBOARDING());
          return null;
        }

        return (
          <ResumeOnboarding
            onPrev={onPrev}
            onRestart={() => send(model.events.RESTART_ONBOARDING())}
            onResume={() => send(model.events.RESUME_ONBOARDING())}
            storedCompany={storedCompany}
            user={userProfile}
          />
        );
      }

      case State.SEARCH:
        return (
          <Search
            onGoCapitalDeposit={() => send(model.events.GO_CAPITAL_DEPOSIT())}
            onPrev={onPrev}
            onSelectCompany={(company) =>
              send(model.events.SUBMIT_EXTERNAL_COMPANY(company))
            }
            onSkip={() => send(model.events.SKIP_EXTERNAL_COMPANY())}
          />
        );

      case 'manual.legal-form-question':
        return (
          <LegalFormQuestion
            onContinue={({ businessForm }) =>
              send(model.events.SUBMIT_BUSINESS_FORM(businessForm))
            }
            onPrev={onPrev}
          />
        );

      case 'manual.identity-info':
      case 'auto.identity-info':
      case 'validation.edit-identity-info':
        return (
          <IdentityInfoEdit
            company={companyProfile}
            isEditing={isEditing}
            onContinue={(payload) =>
              send(model.events.SUBMIT_IDENTITY_INFO(payload))
            }
            onPrev={onPrev}
          />
        );

      case 'manual.legal-info':
      case 'auto.legal-info':
      case 'validation.edit-legal-info':
        return (
          <LegalInfoEdit
            company={companyProfile}
            isEditing={isEditing}
            onContinue={(payload) =>
              send(model.events.SUBMIT_LEGAL_INFO(payload))
            }
            onPrev={onPrev}
          />
        );

      case 'auto.is-micro':
        return (
          <IsMicro
            onContinue={({ isMicro }) =>
              send(model.events.SUBMIT_IS_MICRO(isMicro))
            }
            onPrev={onPrev}
          />
        );

      case 'validation.info-verification':
        return (
          <InfoVerification
            company={companyProfile}
            onEditIdentityInfo={() => {
              send(model.events.EDIT_IDENTITY_INFO());
            }}
            onEditLegalInfo={() => {
              send(model.events.EDIT_LEGAL_INFO());
            }}
            onPrev={onPrev}
            onValidate={() => {
              send(model.events.VALIDATE_INFORMATION());
            }}
          />
        );

      case 'validation.ape-details':
        return (
          <ApeDetails
            company={companyProfile}
            onPrev={onPrev}
            onSave={(values) =>
              send(model.events.SUBMIT_APE_DETAILS(values.activityDetails))
            }
          />
        );

      case State.COMPANY_INELIGIBLE:
        return (
          <CompanyIneligible
            onContinue={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );

      case State.CONTACT_SUPPORT:
        return <ContactSupport onPrev={onPrev} />;

      case State.SUBMISSION:
      case State.INIT:
      case State.COMPANY_INFO_RESTORED:
      case State.DONE:
      case State.ABORT:
      case State.ABORT_TO_CAPITAL_DEPOSIT:
      case 'manual.init':
      case 'manual.done':
      case 'auto.init':
      case 'auto.done':
      case 'validation.init':
      case 'validation.done':
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

export default BusinessInfo;
